import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { appendQueryString, isMobileApp, logException, ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import EditPassword from 'Components/EditPassword';
import Modal from 'Components/Modal';
const { EVENTS } = analytics;

const Settings = ({
  applicationConfig,
  email,
  hasNative,
  hasProfile,
  isVerified,
  name,
  strings,
  uim,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const [modal, setModal] = useState(false);
  const [newPasswordSaved, setNewPasswordSaved] = useState(false);
  const [resendEmailFailed, setResendEmailFailed] = useState(false);
  const [resendEmailSent, setResendEmailSent] = useState(false);

  const modalOpen = () => {
    analytics.event({
      action: EVENTS.terminateAccount,
      label: 'terminateAccount',
    });
    setModal(true);
  };

  const modalClose = () => setModal(false);

  const deleteAccount = async () => {
    analytics.event({
      action: EVENTS.confirmTerminateAccount,
      label: 'confirmTerminateAccount',
    });
    await uim.deleteAccount();
  };

  const editPasswordSubmit = () => {
    setEditMode(false);
    setNewPasswordSaved(true);
    setTimeout(() => {
      setNewPasswordSaved(false);
    }, 5000);
  };

  const requestPasswordSet = async () => {
    try {
      await uim.uimApi.passwordReset(email);
      setEmailed(true);
    } catch (err) {
      // RBMN-6263 let's swallow error message to expose less info for security
      logException(err);
    }
  };

  const resendEmail = async () => {
    analytics.event({
      action: EVENTS.resendVerification,
      label: 'resendVerification',
    });
    setResendEmailFailed(false);
    setResendEmailSent(false);
    try {
      await uim.resendVerificationEmail();
      setResendEmailSent(true);
    } catch {
      setResendEmailFailed(true);
    }
  };

  const renderSendVerificationEmail = () => {
    if (resendEmailFailed)
      return (
        <span className="error-red">
          {strings.verificationEmailFailed}&nbsp;
        </span>
      );

    if (resendEmailSent)
      return (
        <span>
          {strings.verificationSentMessage.profileVerificationMessage}&nbsp;
        </span>
      );

    return (
      <span>
        {strings.verificationSentMessage.formatUimTranslation(email)}&nbsp;
      </span>
    );
  };

  return (
    <div className="settings">
      <div className="sidebar-view__header desktop__header">
        <h1 className="sidebar-view__heading">{strings.settings}</h1>
      </div>

      <div className="sidebar-view__inner">
        <div className="row__label">{strings.name}</div>
        <div className="row__content">{name}</div>
        <div className="row__label">{strings.email}</div>
        <div className="row__content">{email}</div>
        {hasNative && !editMode && (
          <div>
            <div className="row__label">
              <span>{strings.password}</span>
              <span
                className={`row__alert ${newPasswordSaved ? 'show' : 'hide'}`}
              >
                {strings.saved}
              </span>
              <span className="row__action" onClick={() => setEditMode(true)}>
                {strings.edit}
              </span>
            </div>
            <div className="row__content">********</div>
          </div>
        )}
        {hasProfile && !hasNative && !isMobileApp(applicationConfig) && (
          <div>
            <div className="row__label">
              <span>{strings.password}</span>
              {!emailed && (
                <span className="row__action" onClick={requestPasswordSet}>
                  {strings.createPassword}
                </span>
              )}
            </div>
            {emailed ? (
              <div className="row__content">
                <span>
                  {strings.setPasswordEmailSent} {strings.didNotReceive}{' '}
                </span>
                <span className="link--inline" onClick={requestPasswordSet}>
                  {strings.resendEmail}
                </span>
              </div>
            ) : (
              <div className="row__content">{strings.noPassword}</div>
            )}
          </div>
        )}
        {editMode && (
          <EditPassword
            cancel={strings.cancel}
            className="sidebar-view__form"
            confirm={strings.confirm}
            currentPassword={strings.currentPassword}
            errorMessage={strings.errorMessage}
            hideText={strings.hide}
            newPassword={strings.newPassword}
            onCancel={() => setEditMode(false)}
            onSubmit={editPasswordSubmit}
            passwordLength={strings.passwordLength}
            required={strings.required}
            showText={strings.show}
            uim={uim}
          />
        )}
        {!isVerified && (
          <div>
            <div className="row__label">{strings.profileVerificationLabel}</div>
            <div className="row__content">
              {renderSendVerificationEmail()}
              <span className="link--inline" onClick={resendEmail}>
                {strings.resendEmail}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="sidebar-view__buttons">
        {!isMobileApp(applicationConfig) && (
          <Link
            className="button btn--secondary btn--short"
            to={appendQueryString(ROUTES.PAGE_LOGOUT.url)}
          >
            {strings.signOut}
          </Link>
        )}
        <button className="btn--secondary btn--short" onClick={modalOpen}>
          {strings.terminateAccount}
        </button>
      </div>

      {modal && (
        <Modal
          cancel={strings.terminateNo}
          confirm={strings.terminateYes}
          heading={strings.areYouSure}
          message={strings.terminating1}
          message2={strings.terminating2}
          onClose={modalClose}
          onConfirm={deleteAccount}
        />
      )}
    </div>
  );
};

Settings.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  email: PropTypes.string.isRequired,
  hasNative: PropTypes.bool.isRequired,
  hasProfile: PropTypes.bool.isRequired,
  isVerified: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

Settings.defaultProps = {};

export default Settings;
